import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Tag, Typography, Empty, Spin, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { CrmChangeAction, CrmChangeLog, CrmEntityType, CrmFieldChange } from '../../api/CRMApiClient';
import { formatHistoryDate, formatHistoryValue, historyFieldLabelKey } from '../../helpers/crmHistoryFormat';

const { Text } = Typography;

const ACTION_LABEL_KEYS: Record<CrmChangeAction, string> = {
  Created: 'HistoryActionCreated',
  Updated: 'HistoryActionUpdated',
  Deleted: 'HistoryActionDeleted',
  AttachmentAdded: 'HistoryActionAttachmentAdded',
  AttachmentRemoved: 'HistoryActionAttachmentRemoved',
};

const ACTION_COLORS: Record<CrmChangeAction, string> = {
  Created: 'green',
  Updated: 'blue',
  Deleted: 'red',
  AttachmentAdded: 'cyan',
  AttachmentRemoved: 'orange',
};

interface Props {
  entityType: CrmEntityType;
  entityId: number;
  /** Load (and reload) when the tab becomes visible, so a save on the details tab is reflected. */
  active: boolean;
}

const CrmHistoryTab: React.FC<Props> = observer(({ entityType, entityId, active }) => {
  const { t } = useTranslation();
  const { crmStore } = useStore();
  const [rows, setRows] = useState<CrmChangeLog[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await crmStore.getCrmHistory(entityType, entityId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, entityType, entityId]);

  const columns = [
    { title: t('Version'), dataIndex: 'version', key: 'version', width: 90, render: (v: number) => `v${v}` },
    { title: t('ChangedAt'), dataIndex: 'createdAt', key: 'createdAt', width: 160, render: (d: string) => formatHistoryDate(d) },
    { title: t('ChangedBy'), dataIndex: 'userName', key: 'userName', render: (n?: string) => n || t('Unknown') },
    {
      title: t('Action'),
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (a: CrmChangeAction) => <Tag color={ACTION_COLORS[a]}>{t(ACTION_LABEL_KEYS[a] ?? a)}</Tag>,
    },
    { title: t('Changes'), key: 'count', width: 100, render: (_: unknown, r: CrmChangeLog) => r.changes.length },
  ];

  const changeColumns = [
    { title: t('Field'), dataIndex: 'field', key: 'field', width: '30%', render: (f: string) => t(historyFieldLabelKey(f)) },
    { title: t('OldValue'), dataIndex: 'oldValue', key: 'oldValue', render: (v?: string) => <Text delete={!!v}>{formatHistoryValue(v)}</Text> },
    { title: t('NewValue'), dataIndex: 'newValue', key: 'newValue', render: (v?: string) => <Text strong>{formatHistoryValue(v)}</Text> },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ paddingTop: 16 }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={load}>{t('Refresh')}</Button>
        </div>
        <Table
          className="crm-table"
          dataSource={rows}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: <Empty description={t('NoHistoryYet')} /> }}
          expandable={{
            rowExpandable: r => r.changes.length > 0,
            expandedRowRender: r => (
              <Table<CrmFieldChange>
                dataSource={r.changes}
                columns={changeColumns}
                rowKey="field"
                size="small"
                pagination={false}
              />
            ),
          }}
        />
      </div>
    </Spin>
  );
});

export default CrmHistoryTab;
